/**
 * Balance Check Service
 *
 * Frontend service for analyzing homebrew mechanic balance.
 * Communicates with Firebase Cloud Function `analyzeHomebrewBalance`.
 *
 * Features:
 * - Lighthouse-style balance analysis of composed mechanics
 * - Rate limit tracking with credits remaining
 * - Toast notifications for user feedback
 * - Graceful error handling
 *
 * Part of the Homebrew Workshop (Issue #82, Phase F: #181)
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import {
  trackBalanceCheckRequested,
  trackBalanceCheckSuccess,
  trackBalanceCheckFailed,
} from './analyticsService';

/**
 * Analyze the balance of a composed homebrew mechanic.
 *
 * @param {Object} mechanicJSON - The full composed mechanic config object
 * @param {string} userId - Firebase auth UID (for rate limiting)
 * @returns {Promise<Object>} { success, report, creditsRemaining, cached, error, code }
 */
export const analyzeBalance = async (mechanicJSON, userId) => {
  // Validate inputs
  if (!mechanicJSON || typeof mechanicJSON !== 'object') {
    return {
      success: false,
      error: 'No mechanic data provided. Please build a mechanic before checking balance.',
    };
  }

  if (!mechanicJSON?.meta?.name) {
    return {
      success: false,
      error: 'Mechanic must have a name before checking balance.',
    };
  }

  // Show loading toast
  const loadingToastId = toast.loading('Analyzing balance...');

  // Track request in analytics
  trackBalanceCheckRequested();

  try {
    // Call Cloud Function
    const functions = getFunctions();
    const analyzeHomebrewBalance = httpsCallable(functions, 'analyzeHomebrewBalance');

    const result = await analyzeHomebrewBalance({
      mechanicJSON,
      userId,
    });

    const data = result.data;

    // Track success in analytics
    trackBalanceCheckSuccess(data.cached || false);

    // Success toast
    toast.success(
      data.cached
        ? 'Balance check retrieved from cache'
        : 'Balance check complete',
      { id: loadingToastId },
    );

    return {
      success: true,
      report: data.report,
      creditsRemaining: data.creditsRemaining,
      cached: data.cached || false,
    };
  } catch (error) {
    console.error('Balance check error:', error);

    // Parse error message
    let errorMessage = 'Balance check failed. Please try again.';
    let creditsRemaining = null;
    let errorType = 'unknown';

    if (error.code === 'resource-exhausted') {
      errorMessage = 'Daily balance check limit reached. Try again tomorrow.';
      creditsRemaining = 0;
      errorType = 'rate_limit';
    } else if (error.code === 'unauthenticated') {
      errorMessage = 'You must be logged in to use balance check.';
      errorType = 'unauthenticated';
    } else if (error.code === 'invalid-argument') {
      errorMessage = error.message || errorMessage;
      errorType = 'invalid_input';
    } else if (error.code === 'deadline-exceeded' || error.code === 'unavailable') {
      errorMessage = 'Balance check service is temporarily unavailable.';
      errorType = 'timeout';
    }

    // Track failure in analytics
    trackBalanceCheckFailed(errorType);

    // Show error toast
    toast.error(errorMessage, { id: loadingToastId, duration: 5000 });

    return {
      success: false,
      error: errorMessage,
      creditsRemaining,
      code: error.code,
    };
  }
};
